import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config/config.js";
import { useAuth } from "../login/AuthContext";
import { toast } from "react-toastify";
import CustomerStep from "../orders/steps/CustomerStep.jsx";
import { isPaketEtkin } from "../../utils/paketEtkin.js";
import "../customers/customers.css";
import "../orders/orderForm/OrderForm.css";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ExtensionIcon from "@mui/icons-material/Extension";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

export default function PdksOrderForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, userData } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [loading, setLoading] = useState(!!id);
  const [initialLoadDone, setInitialLoadDone] = useState(!id);
  const [saving, setSaving] = useState(false);
  const [paketler, setPaketler] = useState([]);
  const [paketEkModuller, setPaketEkModuller] = useState([]);
  const [pdksLisanslar, setPdksLisanslar] = useState([]);
  const [customer, setCustomer] = useState({ customer: {} });
  const preserveEkModulSelectionRef = useRef(false);
  const [form, setForm] = useState({
    PaketId: "",
    LisansTipi: "Yıllık",
    Durum: 1,
    IsNewLicense: true,
    PdksLisansId: "",
    Aciklama: "",
    selectedEkModulIds: [],
    KullaniciSayisi: 1,
    PersonelSayisi: 10,
  });
  const [customerFiles, setCustomerFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);

  useEffect(() => {
    axios
      .get(`${config.baseURL}/pdks-paketler`, { headers })
      .then((r) => setPaketler(r.data || []))
      .catch(() => {});
  }, [headers]);

  const aktifPaketler = useMemo(
    () => paketler.filter((p) => isPaketEtkin(p) || String(p.Id) === String(form.PaketId)),
    [paketler, form.PaketId],
  );

  const loadPdksLicenses = useCallback(
    async (musteriId) => {
      if (!musteriId) {
        setPdksLisanslar([]);
        return [];
      }
      try {
        const res = await axios.get(`${config.baseURL}/pdks-lisanslar/musteri-lisans/${musteriId}`, { headers });
        const list = Array.isArray(res.data) ? res.data : [];
        setPdksLisanslar(list);
        return list;
      } catch {
        setPdksLisanslar([]);
        return [];
      }
    },
    [headers],
  );

  useEffect(() => {
    if (!id) {
      setInitialLoadDone(true);
      return;
    }

    let cancelled = false;
    setLoading(true);

    axios
      .get(`${config.baseURL}/pdks-siparis/${id}`, { headers })
      .then(async (res) => {
        if (cancelled) return;
        const d = res.data;
        let customerPayload = {
          Unvan: d.Unvan || "",
          Yetkili: d.Yetkili || "",
          Tel: d.Tel || "",
          Tel2: d.Tel2 || "",
          Faks: d.Faks || "",
          Fax: d.Faks || "",
          EPosta: d.EPosta || "",
          Adres: d.Adres || "",
          Il: d.Il || "",
          Ilce: d.Ilce || "",
          MusteriNo: d.MusteriNo || "",
          VergiNo: d.VergiNo || "",
          VergiDairesi: d.VergiDairesi || "",
          TCKimlikNo: d.TCKimlikNo || "",
          BayiKodu: userData?.BayiKodu,
        };
        if (d.MusteriNo) {
          try {
            const mRes = await axios.get(`${config.baseURL}/musteriler/search`, {
              headers,
              params: { query: d.MusteriNo, limit: 5, status: "aktif" },
            });
            const match = (mRes.data?.data || []).find((m) => m.MusteriNo === d.MusteriNo);
            if (match?.Id) {
              customerPayload = {
                ...customerPayload,
                Id: match.Id,
                Faks: match.Faks ?? customerPayload.Faks,
                Fax: match.Faks ?? match.Fax ?? customerPayload.Fax,
              };
              await loadPdksLicenses(match.Id);
            }
          } catch {
            /* ignore */
          }
        }
        setCustomer({ customer: customerPayload });
        setForm({
          PaketId: d.PaketId || "",
          LisansTipi: d.LisansTipi === "Demo" ? "Demo" : "Yıllık",
          Durum: d.Durum ?? 1,
          IsNewLicense: d.IsNewLicense !== false,
          PdksLisansId: d.PdksLisansId || "",
          Aciklama: d.Aciklama || "",
          selectedEkModulIds: d.selectedEkModulIds || [],
          KullaniciSayisi: Number(d.KullaniciSayisi) > 0 ? Number(d.KullaniciSayisi) : 1,
          PersonelSayisi: Number(d.PersonelSayisi) > 0 ? Number(d.PersonelSayisi) : 10,
        });
        setCustomerFiles(
          (d.Dokumanlar || []).map((doc) => ({
            dokumanId: doc.Id,
            name: doc.DosyaAdi,
            systemName: doc.SistemDosyaAdi,
            type: "application/pdf",
            size: doc.DosyaBoyutu,
            url: `${config.baseURL}/pdks-siparis/download/${doc.SistemDosyaAdi}`,
          })),
        );
        setFilesToDelete([]);
        preserveEkModulSelectionRef.current = true;
      })
      .catch(() => {
        if (!cancelled) toast.error("Sipariş yüklenemedi");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setInitialLoadDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, headers, loadPdksLicenses, userData?.BayiKodu]);

  useEffect(() => {
    if (!form.PaketId) {
      setPaketEkModuller([]);
      return;
    }
    axios
      .get(`${config.baseURL}/pdks-paketler/paket-detay/${form.PaketId}`, { headers })
      .then((res) => {
        const ekList = res.data?.EkModuller || [];
        setPaketEkModuller(ekList);
        const paket = res.data || {};
        if (preserveEkModulSelectionRef.current) {
          preserveEkModulSelectionRef.current = false;
          return;
        }
        setForm((prev) => ({
          ...prev,
          selectedEkModulIds: [],
          KullaniciSayisi: Number(paket.MaxKullaniciSayisi) > 0 ? Number(paket.MaxKullaniciSayisi) : prev.KullaniciSayisi,
          PersonelSayisi: Number(paket.MaxPersonnel) > 0 ? Number(paket.MaxPersonnel) : prev.PersonelSayisi,
        }));
      })
      .catch(() => setPaketEkModuller([]));
  }, [form.PaketId, headers]);

  const handleCustomerChange = useCallback(
    async (name, value) => {
      if (name === "customer") {
        setCustomer({
          customer: {
            ...value,
            BayiKodu: userData?.BayiKodu,
          },
        });
        if (value?.Id) {
          const licenses = await loadPdksLicenses(value.Id);
          setForm((prev) => ({
            ...prev,
            PdksLisansId: !prev.IsNewLicense && licenses[0] ? String(licenses[0].Id) : prev.PdksLisansId,
          }));
        } else {
          setPdksLisanslar([]);
          setForm((prev) => ({ ...prev, PdksLisansId: prev.IsNewLicense ? "" : prev.PdksLisansId }));
        }
      }
    },
    [loadPdksLicenses, userData?.BayiKodu],
  );

  const toggleEkModul = (ekModulId) => {
    setForm((prev) => {
      const set = new Set(prev.selectedEkModulIds);
      if (set.has(ekModulId)) set.delete(ekModulId);
      else set.add(ekModulId);
      return { ...prev, selectedEkModulIds: [...set] };
    });
  };

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    const valid = [];
    for (const file of selected) {
      if (file.type !== "application/pdf") {
        toast.error("Yalnızca PDF dosyaları yüklenebilir.");
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Maksimum dosya boyutu 10MB olmalıdır.");
        continue;
      }
      valid.push(file);
    }
    setCustomerFiles((prev) => [...prev, ...valid]);
    event.target.value = "";
  };

  const handleRemoveFile = (index, dokumanId) => {
    if (dokumanId) {
      setFilesToDelete((prev) => [...prev, dokumanId]);
      setCustomerFiles((prev) =>
        prev.map((file, i) => (i === index ? { ...file, isDeleted: true } : file)),
      );
    } else {
      setCustomerFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return "";
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleSave = async (durumOverride) => {
    const c = customer.customer || {};
    if (!c.Unvan?.trim()) {
      toast.warning("Müşteri ünvanı zorunlu");
      return;
    }
    if (!form.PaketId) {
      toast.warning("Paket seçimi zorunlu");
      return;
    }
    if (!form.IsNewLicense && !form.PdksLisansId) {
      toast.warning("Lisans yenileme için PDKS lisansı seçin");
      return;
    }
    const nextDurum = durumOverride ?? form.Durum;
    const activeFiles = customerFiles.filter((f) => !f.isDeleted);
    if (Number(nextDurum) === 2 && activeFiles.length === 0) {
      toast.warning("Siparişi onaylamak için en az bir PDF ekleyin (onaylı teklif).");
      return;
    }
    const invalid = activeFiles.filter((f) => f instanceof File && f.type !== "application/pdf");
    if (invalid.length > 0) {
      toast.error("Yalnızca PDF dosyaları yüklenebilir.");
      return;
    }
    const userCount = Math.max(1, parseInt(form.KullaniciSayisi, 10) || 1);
    const personelCount = Math.max(1, parseInt(form.PersonelSayisi, 10) || 1);
    const payload = {
      Unvan: c.Unvan,
      Yetkili: c.Yetkili,
      Tel: c.Tel,
      Tel2: c.Tel2,
      Faks: c.Faks || c.Fax,
      EPosta: c.EPosta,
      Adres: c.Adres,
      Il: c.Il,
      Ilce: c.Ilce,
      MusteriNo: c.MusteriNo || null,
      VergiNo: c.VergiNo,
      VergiDairesi: c.VergiDairesi,
      TCKimlikNo: c.TCKimlikNo,
      PaketId: form.PaketId ? parseInt(form.PaketId, 10) : null,
      Durum: durumOverride ?? form.Durum,
      LisansTipi: form.LisansTipi,
      IsNewLicense: form.IsNewLicense,
      PdksLisansId: form.PdksLisansId ? parseInt(form.PdksLisansId, 10) : null,
      Aciklama: form.Aciklama,
      selectedEkModulIds: form.selectedEkModulIds,
      KullaniciSayisi: userCount,
      PersonelSayisi: personelCount,
      filesToDelete,
    };
    const fd = new FormData();
    fd.append("payload", JSON.stringify(payload));
    activeFiles.forEach((file) => {
      if (file instanceof File) fd.append("customerFiles", file);
    });
    try {
      setSaving(true);
      let res;
      if (id) {
        res = await axios.put(`${config.baseURL}/pdks-siparis/${id}`, fd, { headers });
      } else {
        res = await axios.post(`${config.baseURL}/pdks-siparis`, fd, { headers });
      }
      if (res.data?.lisansKey) {
        toast.success(`Onaylandı. PDKS Lisans: ${res.data.lisansKey}`);
      } else {
        toast.success("Kaydedildi");
      }
      navigate("/pdks/siparisler");
    } catch (e) {
      const detail = e?.response?.data?.error;
      const msg = e?.response?.data?.message || "Kayıt başarısız";
      toast.error(detail && !msg.includes(detail) ? `${msg} (${detail})` : msg);
    } finally {
      setSaving(false);
    }
  };

  const isApproved = form.Durum === 2;

  if (!initialLoadDone) {
    return (
      <div className="cari-listesi-container">
        <CircularProgress sx={{ m: 4 }} />
      </div>
    );
  }

  return (
    <div className="cari-listesi-container">
      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
          mx: "auto",
          p: 3,
          position: "relative",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.6)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          {id ? "PDKS Sipariş Düzenle" : "PDKS Sipariş Ekle"}
        </Typography>

        {isApproved && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
            Onaylanmış PDKS siparişinde müşteri bilgileri kilitlidir. Paket ve lisans alanlarını kontrol edin.
          </Alert>
        )}

        <CustomerStep
          key={id || "new"}
          onChange={handleCustomerChange}
          initialData={customer}
          orderId={null}
          skipOrderFetch
          allowEditExistingCustomer={!isApproved}
          isDisabled={isApproved}
          isEditMode={!!id}
          customer={customer}
        />

        <Card elevation={0} sx={{ my: 1.5 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Inventory2Icon color="action" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}>
                PDKS Paket & Lisans
              </Typography>
            </Box>
            <Divider sx={{ mb: 2, mt: 0.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Paket</InputLabel>
                  <Select
                    label="Paket"
                    value={form.PaketId}
                    disabled={isApproved}
                    onChange={(e) => setForm({ ...form, PaketId: e.target.value })}
                  >
                    <MenuItem value="">Seçiniz</MenuItem>
                    {aktifPaketler.map((p) => (
                      <MenuItem key={p.Id} value={p.Id}>
                        {p.Adi} ({p.Edition})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Lisans Tipi</InputLabel>
                  <Select
                    label="Lisans Tipi"
                    value={form.LisansTipi}
                    disabled={isApproved}
                    onChange={(e) => setForm({ ...form, LisansTipi: e.target.value })}
                  >
                    <MenuItem value="Yıllık">Yıllık</MenuItem>
                    <MenuItem value="Demo">Demo (15 gün)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Sipariş Tipi</InputLabel>
                  <Select
                    label="Sipariş Tipi"
                    disabled={isApproved}
                    value={form.IsNewLicense ? "new" : "renew"}
                    onChange={(e) => {
                      const isNew = e.target.value === "new";
                      setForm((prev) => ({
                        ...prev,
                        IsNewLicense: isNew,
                        PdksLisansId: isNew
                          ? ""
                          : pdksLisanslar[0]?.Id
                            ? String(pdksLisanslar[0].Id)
                            : prev.PdksLisansId,
                      }));
                    }}
                  >
                    <MenuItem value="new">Yeni Lisans</MenuItem>
                    <MenuItem value="renew">Lisans Yenileme</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Personel sayısı"
                  disabled={isApproved}
                  inputProps={{ min: 1, step: 1 }}
                  value={form.PersonelSayisi}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      PersonelSayisi: Math.max(1, parseInt(e.target.value, 10) || 1),
                    })
                  }
                  helperText="Lisans üst personel limiti"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Toplam kullanıcı sayısı"
                  disabled={isApproved}
                  inputProps={{ min: 1, step: 1 }}
                  value={form.KullaniciSayisi}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      KullaniciSayisi: Math.max(1, parseInt(e.target.value, 10) || 1),
                    })
                  }
                  helperText="PDKS panele giriş hakkı"
                />
              </Grid>
              {form.PaketId && paketEkModuller.length > 0 && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ExtensionIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Özellikler / ek modüller
                    </Typography>
                  </Box>
                  <FormGroup row>
                    {paketEkModuller.map((em) => (
                      <FormControlLabel
                        key={em.Id}
                        control={
                          <Checkbox
                            checked={form.selectedEkModulIds.map(Number).includes(Number(em.Id))}
                            disabled={isApproved}
                            onChange={() => toggleEkModul(em.Id)}
                          />
                        }
                        label={em.Adi}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}
              {!form.IsNewLicense && (
                <Grid item xs={12} md={8}>
                  {pdksLisanslar.length > 0 ? (
                    <FormControl fullWidth>
                      <InputLabel>Yenilenecek PDKS Lisansı</InputLabel>
                      <Select
                        label="Yenilenecek PDKS Lisansı"
                        value={form.PdksLisansId ? String(form.PdksLisansId) : ""}
                        onChange={(e) => setForm({ ...form, PdksLisansId: e.target.value })}
                      >
                        {pdksLisanslar.map((lic) => (
                          <MenuItem key={lic.Id} value={String(lic.Id)}>
                            {lic.LisansKey} · {lic.Edition || "—"} · {lic.Etkin ? "Aktif" : "Pasif"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="PDKS Lisans ID"
                      value={form.PdksLisansId}
                      onChange={(e) => setForm({ ...form, PdksLisansId: e.target.value })}
                      helperText={
                        customer.customer?.Id
                          ? "Bu müşteride PDKS lisansı bulunamadı"
                          : "Mevcut müşteri seçerseniz lisanslar listelenir"
                      }
                    />
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Açıklama"
                  value={form.Aciklama}
                  onChange={(e) => setForm({ ...form, Aciklama: e.target.value })}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <AttachFileIcon color="action" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}>
                Onaylı teklif / müşteri dosyası (PDF)
              </Typography>
            </Box>
            <Divider sx={{ mb: 2, mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              İlk kayıtta PDF isteğe bağlıdır. Siparişi onaylarken en az bir PDF (onaylı teklif) zorunludur.
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={isApproved}
              sx={{ mb: 2 }}
            >
              Dosya Yükle
              <input type="file" accept="application/pdf" multiple hidden onChange={handleFileChange} />
            </Button>
            <Stack spacing={1}>
              {customerFiles.filter((f) => !f.isDeleted).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Henüz dosya eklenmedi
                </Typography>
              ) : (
                customerFiles.map((file, index) =>
                  file.isDeleted ? null : (
                    <Paper
                      key={file.dokumanId || `${file.name}-${index}`}
                      variant="outlined"
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1 }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <PictureAsPdfIcon color="error" />
                        <Box>
                          <Typography variant="body2">{file.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                      </Box>
                      {!isApproved && (
                        <IconButton size="small" color="error" onClick={() => handleRemoveFile(index, file.dokumanId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Paper>
                  ),
                )
              )}
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap", pb: 4 }}>
          <Button variant="outlined" onClick={() => navigate("/pdks/siparisler")}>
            İptal
          </Button>
          {!isApproved && (
            <>
              <Button variant="contained" disabled={saving} onClick={() => handleSave(1)}>
                Kaydet (Beklemede)
              </Button>
              <Button variant="contained" color="success" disabled={saving} onClick={() => handleSave(2)}>
                Onayla & Lisans Aç
              </Button>
            </>
          )}
          {isApproved && (
            <Button variant="contained" disabled={saving} onClick={() => handleSave(2)}>
              Lisansı Yeniden İşle
            </Button>
          )}
        </Box>
      </Box>
    </div>
  );
}
